import type { ReactNode } from "react";
import { SlideEyebrowTab } from "./SlideEyebrowTab";

/**
 * The statement band — rows A+B of the five-row template (see SlideGrid.ts).
 *
 * Three parts, in this order:
 *
 *   1. The eyebrow, as a filled tab flush to the card's own top-left corner.
 *      It escapes the slide's padding with negative margins rather than the
 *      slide dropping its padding, so every other slide is untouched. Only the
 *      bottom-right corner is rounded: the other three are the card's edge.
 *   2. The h1, vertically centred in whatever height is left. A short claim
 *      gets the same air as a long one, which is what makes the band read as
 *      deliberate rather than as leftover space.
 *   3. A 1px rule, full-bleed across the card.
 *
 * The rule is the template's argument made literal: everything above it is the
 * one claim the slide exists to make, and everything below it — the strap
 * included — is the substantiation for a reader who wants it. So the strap
 * lives in the slide body, NOT in this component.
 */
export function SlideStatement({
  eyebrow,
  unnumbered,
  children,
}: {
  eyebrow: string;
  /** Suppress the auto "NN _ " prefix — the lettered appendix slides. */
  unnumbered?: boolean;
  children: ReactNode;
}) {
  return (
    <header className="flex flex-col md:h-[346px] md:shrink-0">
      <SlideEyebrowTab eyebrow={eyebrow} unnumbered={unnumbered} />

      <div className="flex flex-1 items-center py-6 md:py-0">{children}</div>

      {/* Below md the slide body follows the rule immediately, with none of
          the desktop grid's breathing room — so the rule carries the gap
          itself, and gives it back at md (TPF, 2026-09-09). */}
      <div
        aria-hidden="true"
        className="-mx-6 mb-6 h-px shrink-0 bg-slate-300 md:-mx-24 md:mb-0"
      />
    </header>
  );
}
